import {
  DefaultOptions,
  QueryClient,
  QueryClientProvider,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosInstance } from "axios";
import * as React from "react";

import { ServerConfig, Environment, PortalClientLogger } from "../types";
import createServer from "./createServer";
import serverConfig from "./serverConfig";

interface BaseServerProviderProps {
  appAgent?: string;
  debug?: boolean;
  deviceId?: string;
  queryClientOptions?: DefaultOptions;
  logger?: PortalClientLogger;
  version?: "v1";
  getAccessToken: () => Promise<string>;
}

interface EnvironmentServerProviderProps extends BaseServerProviderProps {
  environment?: Environment;
  customServerConfig?: never;
}

interface CustomServerConfigServerProviderProps
  extends BaseServerProviderProps {
  environment?: never;
  customServerConfig?: ServerConfig;
}

export type ServerProviderProps =
  | EnvironmentServerProviderProps
  | CustomServerConfigServerProviderProps;

export interface ServerContextValue {
  logger?: PortalClientLogger;
  server: AxiosInstance;
  unauthenticatedServer: AxiosInstance;
}

const ServerContext = React.createContext<ServerContextValue | undefined>(
  undefined
);

const queryClient = new QueryClient();

export let PCLogger: PortalClientLogger = {
  debug: console.debug,
  info: console.info,
  warn: console.warn,
  error: console.error,
};

export const ServerProvider: React.FunctionComponent<ServerProviderProps> = ({
  appAgent = "Achievement",
  children,
  debug = false,
  deviceId,
  environment = "development",
  customServerConfig,
  getAccessToken = () => Promise.resolve("portal-token"),
  logger,
  queryClientOptions,
  version = "v7",
}) => {
  const contextValue = React.useMemo(() => {
    const activeServerConfig = customServerConfig ?? serverConfig[environment];
    const server = createServer({
      appAgent,
      axiosRequestConfig: {
        baseURL: `${activeServerConfig.domain}/${version}`,
        withCredentials: true,
        headers: deviceId
          ? {
              "evi-device-id": deviceId,
            }
          : undefined,
      },
      debug,
      getAuthorization: async () => `Bearer ${await getAccessToken()}`,
    });

    const unauthenticatedServer = createServer({
      appAgent,
      axiosRequestConfig: {
        baseURL: `${activeServerConfig.domain}/${version}`,
      },
      debug,
    });

    return { server, unauthenticatedServer };
  }, [
    customServerConfig,
    environment,
    appAgent,
    version,
    deviceId,
    debug,
    getAccessToken,
  ]);

  React.useEffect(() => {
    if (logger) {
      PCLogger = logger;
    }
  }, [logger]);

  React.useEffect(() => {
    if (queryClientOptions) {
      queryClient.setDefaultOptions(queryClientOptions);
    }
  }, [queryClientOptions]);

  return (
    <QueryClientProvider client={queryClient}>
      <ServerContext.Provider value={contextValue}>
        {children}
      </ServerContext.Provider>
    </QueryClientProvider>
  );
};

export const useServerContext = () => {
  const context = React.useContext(ServerContext);

  if (!context) {
    throw new Error("useServerContext must be used within ServerProvider");
  }

  return context;
};

export const useServer = (): AxiosInstance => {
  const context = React.useContext(ServerContext);

  if (!context) {
    throw new Error("useServer must be used within ServerProvider");
  }

  return context.server;
};

export const useUnauthenticatedServer = (): AxiosInstance => {
  const context = React.useContext(ServerContext);

  if (!context) {
    throw new Error("useServer must be used within ServerProvider");
  }

  return context.unauthenticatedServer;
};

export { useQueryClient };

export default ServerContext;
